import moment from 'moment'
import type { QuoteDocument } from 'paintscout'
import type { PainterScheduleEvent } from '../PainterScheduleView'
import type { PainterPayment } from '../PainterScheduleView/PainterScheduleView/types'

const DEMO_QUOTE_PREFIX = 'demo-painter-'

export function isPainterDemoQuote(quote?: Pick<QuoteDocument, '_id'> | null) {
  return !!quote?._id?.startsWith(DEMO_QUOTE_PREFIX)
}

export function buildPainterScheduleMockEvents(
  referenceDate = new Date(),
  currentUserId = 'auth0|demo-painter'
): PainterScheduleEvent[] {
  const weekStart = moment(referenceDate).startOf('week')
  const today = moment(referenceDate)
  const tomorrow = today.clone().add(1, 'day')

  return [
    makeEvent({
      id: 'demo-last-week',
      title: 'Demo: Estimate fallback work order',
      startDate: weekStart.clone().subtract(1, 'week').add(2, 'days').hour(8).minute(0).toDate(),
      endDate: weekStart.clone().subtract(1, 'week').add(2, 'days').hour(15).minute(30).toDate(),
      jobAddress: '221 Deal Ave NW, Calgary, AB',
      dealId: 'demo-deal-last-week',
      quoteId: `${DEMO_QUOTE_PREFIX}estimate-last-week`,
      quoteType: 'quote',
      currentUserId,
      crewNote: 'Estimate-only demo. This represents the pre-invoice fallback work order.',
      contact: {
        name: 'Jordan Lee',
        address: '991 Contact Road, Calgary, AB',
        email: 'jordan@example.com',
        phone: '403-555-0191'
      },
      payments: []
    }),
    makeEvent({
      id: 'demo-today-exterior',
      title: 'Demo: Exterior repaint',
      startDate: today.clone().hour(8).minute(0).toDate(),
      endDate: today.clone().hour(16).minute(0).toDate(),
      jobAddress: '123 Job Street, Calgary, AB',
      dealId: 'demo-deal-exterior',
      quoteId: `${DEMO_QUOTE_PREFIX}invoice-exterior`,
      currentUserId,
      crewNote: 'Use side gate. Mask all black fixtures before spraying.',
      contact: {
        name: 'Alex Morrison',
        address: '789 Contact Street, Calgary, AB',
        email: 'alex@example.com',
        phone: '780-555-0142'
      },
      payments: [
        {
          id: 'demo-payment-office',
          amount: 750,
          date: today.clone().subtract(2, 'days').format('MMM D, YYYY'),
          method: 'Credit Card',
          note: 'Deposit recorded by office.',
          createdByCurrentUser: false,
          source: 'quote'
        },
        {
          id: 'demo-payment-painter',
          amount: 250,
          date: today.clone().format('MMM D, YYYY'),
          method: 'Cash',
          note: 'Painter-created demo payment.',
          createdByCurrentUser: true,
          source: 'quote'
        }
      ],
      additionalWork: [
        {
          id: 'demo-aw-preset',
          name: 'Garage door repaint',
          hours: 3,
          price: 450,
          description: 'Preset option from the original invoice that can be submitted for approval.',
          isCreated: false,
          status: 'pending',
          hasMedia: true,
          media: [
            {
              id: 'demo-media-1',
              type: 'image',
              title: 'Garage door photo',
              url: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be'
            }
          ],
          canDelete: true
        },
        {
          id: 'demo-aw-approved',
          name: 'Fence touch-up',
          hours: 2,
          price: 280,
          description: 'Approved additional work. Painter cannot edit/delete after signing.',
          isCreated: true,
          status: 'approved',
          approvedDate: today.clone().subtract(1, 'day').toISOString(),
          canEdit: false,
          canDelete: false
        }
      ]
    }),
    makeEvent({
      id: 'demo-today-interior',
      title: 'Demo: Interior touch-ups',
      startDate: today.clone().hour(10).minute(30).toDate(),
      endDate: today.clone().hour(13).minute(0).toDate(),
      jobAddress: '45 Suite Lane SW, Calgary, AB',
      dealId: 'demo-deal-interior',
      quoteId: `${DEMO_QUOTE_PREFIX}invoice-interior`,
      currentUserId,
      crewNote: 'Customer is home after 10 AM. Protect hallway floors.',
      contact: {
        name: 'Sam Patel',
        email: 'sam@example.com',
        phone: '587-555-0114'
      }
    }),
    makeEvent({
      id: 'demo-tomorrow-single',
      title: 'Demo: Cabinet enamel',
      startDate: tomorrow.clone().hour(9).minute(0).toDate(),
      endDate: tomorrow.clone().hour(17).minute(0).toDate(),
      jobAddress: '88 Kitchen Court, Calgary, AB',
      dealId: 'demo-deal-cabinets',
      quoteId: `${DEMO_QUOTE_PREFIX}invoice-cabinets`,
      currentUserId,
      crewNote: 'Single-job day. This card should auto-expand when selected.',
      contact: {
        name: 'Morgan Chen',
        email: 'morgan@example.com',
        phone: '403-555-0166'
      }
    }),
    makeEvent({
      id: 'demo-next-week',
      title: 'Demo: Deck staining',
      startDate: weekStart.clone().add(1, 'week').add(3, 'days').hour(8).minute(0).toDate(),
      endDate: weekStart.clone().add(1, 'week').add(3, 'days').hour(14).minute(0).toDate(),
      jobAddress: '17 Cedar Point, Calgary, AB',
      dealId: 'demo-deal-next-week',
      quoteId: `${DEMO_QUOTE_PREFIX}invoice-next-week`,
      currentUserId,
      crewNote: 'Next-week demo job for week navigation.',
      contact: {
        name: 'Taylor Brooks',
        email: 'taylor@example.com',
        phone: '403-555-0188'
      }
    })
  ]
}

function makeEvent(config: {
  id: string
  title: string
  startDate: Date
  endDate: Date
  dealId: string
  quoteId: string
  currentUserId: string
  quoteType?: 'quote' | 'invoice'
  jobAddress?: string
  crewNote?: string
  contact?: PainterScheduleEvent['contact']
  payments?: PainterPayment[]
  additionalWork?: PainterScheduleEvent['additionalWork']
}): PainterScheduleEvent {
  const sourceQuote = makeSourceQuote({
    id: config.quoteId,
    dealId: config.dealId,
    quoteType: config.quoteType ?? 'invoice',
    currentUserId: config.currentUserId,
    payments: config.payments,
    crewNote: config.crewNote
  })

  return {
    id: config.id,
    title: config.title,
    startDate: config.startDate,
    endDate: config.endDate,
    jobAddress: config.jobAddress,
    contact: config.contact,
    totalHours: 14,
    crewNote: config.crewNote,
    workAreaSummary: {
      walls: 640,
      ceilings: 180,
      totalSqft: 820,
      totalLnft: 160
    },
    workItems: [
      {
        id: `${config.id}-prep`,
        name: 'Prep and masking',
        hours: 4,
        subareas: [
          { name: 'Main work area', productName: 'Tape, poly, paper', quantity: '1 setup', coats: 1, hours: 2 },
          { name: 'Fixtures', productName: 'Masking materials', quantity: '12 fixtures', coats: 1, hours: 2 }
        ]
      },
      {
        id: `${config.id}-finish`,
        name: 'Finish coats',
        hours: 10,
        subareas: [
          { name: 'Walls', productName: 'Premium interior/exterior paint', quantity: '820 sqft', coats: 2, hours: 10 }
        ]
      }
    ],
    additionalWork: config.additionalWork ?? [],
    sourceQuote
  }
}

function makeSourceQuote(config: {
  id: string
  dealId: string
  quoteType: 'quote' | 'invoice'
  currentUserId: string
  payments?: PainterPayment[]
  crewNote?: string
}): QuoteDocument {
  const updatedAt = Date.now()

  return {
    _id: config.id,
    deal_id: config.dealId,
    type: config.quoteType,
    is_invoice: config.quoteType === 'invoice',
    version: 6,
    name: '',
    hourly_rate: 0,
    tax_rate: '',
    tax_description: '',
    tax_details: [],
    discount: 0,
    discounts: [],
    discount_key: '',
    discount_type: { value: 'percentage' },
    discount_description: '',
    discount_isShown: false,
    client_note: '',
    int_ext: { value: 'ext' },
    status: { value: 'draft' },
    internal_note: config.crewNote,
    presentationTemplate: null,
    sentReports: {},
    dates: {
      created: updatedAt,
      updated: updatedAt,
      client: {},
      staff: {},
      lastStatusUpdate: updatedAt
    },
    signatures: {},
    clientVisible: true,
    crewVisible: true,
    totals: {
      hours: 14,
      price: 0,
      prep: 0,
      line_items: {
        price: 0,
        hours: 0,
        prep: 0
      },
      groups: {},
      service_fee_visible: 0,
      service_fee_hidden: 0,
      total_discount: 0,
      after_discount: 0,
      hours_after_discount: 0,
      total_tax: 0,
      after_tax: 0,
      additionalWork: { price: 730 }
    },
    followUps: {},
    defaultView: 'area',
    payments: (config.payments ?? []).map((payment) => ({
      id: payment.id,
      amount: payment.amount,
      type: payment.method,
      date: moment(payment.date, 'MMM D, YYYY').valueOf(),
      manual: true,
      createdBy: payment.createdByCurrentUser ? config.currentUserId : 'auth0|demo-office',
      note: payment.note
    })) as any,
    areas: {
      'demo-area-main': {
        key: 'demo-area-main',
        type: 'area',
        quoteType: 'ext',
        areaType: 'surface',
        label: 'Main Work Area',
        show: true,
        section: 'bid',
        area_label: { value: 'Main Work Area' },
        dimensions: {
          width: 24,
          length: 20,
          height: 10
        },
        substrateOrder: [],
        substrates: {},
        totals: {
          hours: {},
          prep: {},
          price: {}
        }
      },
      'demo-area-secondary': {
        key: 'demo-area-secondary',
        type: 'area',
        quoteType: 'ext',
        areaType: 'surface',
        label: 'Secondary Area',
        show: true,
        section: 'bid',
        area_label: { value: 'Secondary Area' },
        dimensions: {
          width: 18,
          length: 16,
          height: 9
        },
        substrateOrder: [],
        substrates: {},
        totals: {
          hours: {},
          prep: {},
          price: {}
        }
      },
      'demo-area-trim': {
        key: 'demo-area-trim',
        type: 'area',
        quoteType: 'ext',
        areaType: 'surface',
        label: 'Trim & Doors',
        show: true,
        section: 'bid',
        area_label: { value: 'Trim & Doors' },
        dimensions: {
          width: 10,
          length: 8,
          height: 8
        },
        substrateOrder: [],
        substrates: {},
        totals: {
          hours: {},
          prep: {},
          price: {}
        }
      }
    },
    lineItems: {
      'demo-line-prep': {
        key: 'demo-line-prep',
        type: 'lineItem',
        section: 'bid',
        show: true,
        label: 'Prep and masking',
        name: 'Prep and masking',
        description: '',
        materials: [],
        files: [],
        hours: 4,
        price: 640,
        value: 640
      },
      'demo-option-garage-door': {
        key: 'demo-option-garage-door',
        type: 'lineItem',
        section: 'options',
        show: true,
        label: 'Garage door repaint',
        name: 'Garage door repaint',
        description: 'Repaint and seal all garage door panels',
        materials: [],
        files: [{ id: 'demo-option-file-1', src: 'https://example.com/demo-garage-door.jpg', title: 'Garage door' }],
        hours: 3,
        price: 450,
        value: 450
      },
      'demo-option-fence': {
        key: 'demo-option-fence',
        type: 'lineItem',
        section: 'options',
        show: true,
        label: 'Fence touch-up',
        name: 'Fence touch-up',
        description: 'Touch up peeling and chipped fence sections',
        materials: [],
        files: [],
        hours: 2,
        price: 280,
        value: 280
      },
      'demo-option-caulking': {
        key: 'demo-option-caulking',
        type: 'lineItem',
        section: 'options',
        show: true,
        label: 'Caulk & seal window frames',
        name: 'Caulk & seal window frames',
        description: 'Recaulk and seal all exterior window frames',
        materials: [],
        files: [{ id: 'demo-option-file-2', src: 'https://example.com/demo-windows.jpg', title: 'Window frames' }],
        hours: 1,
        price: 150,
        value: 150
      }
    },
    substrates: {},
    substratesGroupBy: 'client_label',
    groups: {},
    files: [],
    order: {
      area: [
        { type: 'area', key: 'demo-area-main' },
        { type: 'area', key: 'demo-area-secondary' },
        { type: 'area', key: 'demo-area-trim' },
        { type: 'line_item', key: 'demo-line-prep' },
        { type: 'line_item', key: 'demo-option-garage-door' },
        { type: 'line_item', key: 'demo-option-fence' },
        { type: 'line_item', key: 'demo-option-caulking' }
      ],
      substrate: [],
      crew: []
    },
    options: { showPrices: true },
    terms: {},
    sources: {},
    source: null,
    quote_date: updatedAt
  } as unknown as QuoteDocument
}
